/**
 * The installation planner's logic, kept free of React so it can be tested.
 *
 * Everything here is derived from measurements in the installation guide
 * rather than invented: the throughput figures come from a running deployment
 * and a benchmark on a dedicated instance, and the storage figure from actual
 * recorded sessions. Where a number is a judgement call it says so.
 */

export type Hosting = 'managed' | 'self' | 'private' | 'airgapped';
export type Cloud = 'aws' | 'azure' | 'gcp' | 'vmware' | 'other';
export type Runtime = 'compose' | 'kubernetes' | 'openshift';
export type Provisioner = 'terraform' | 'cloudformation' | 'helm' | 'compose' | 'tarball';

export interface Answers {
  hosting: Hosting;
  /** Only meaningful when hosting is 'private'. */
  cloud: Cloud;
  runtime: Runtime;
  provisioner: Provisioner;
  /** Recorded sessions per month, the number a customer can actually estimate. */
  sessionsPerMonth: number;
  /** Days of retention their policy requires. */
  retentionDays: number;
  /** Enterprise PII masking — the one feature that changes the machine. */
  piiMasking: boolean;
  /** A managed database instead of the bundled container. */
  managedDatabase: boolean;
}

export const DEFAULT_ANSWERS: Answers = {
  hosting: 'private',
  cloud: 'aws',
  runtime: 'compose',
  provisioner: 'terraform',
  sessionsPerMonth: 100_000,
  retentionDays: 90,
  piiMasking: false,
  managedDatabase: false,
};

export interface Sizing {
  vcpu: number;
  ramGb: number;
  /** Root/system disk, excluding object storage. */
  diskGb: number;
  /** Object storage for recordings, in GB. */
  objectStorageGb: number;
  /** Rough peak concurrent visitors the sizing is good for. */
  peakConcurrent: number;
  /** True when the load is light enough that a burstable instance is honest. */
  burstableOk: boolean;
}

export interface Recommendation {
  sizing: Sizing;
  /** Instance type for the chosen cloud, or null when there is no cloud. */
  instance: string | null;
  /** Kubernetes replica counts, when the runtime is a cluster. */
  replicas: { api: number; collector: number; worker: number } | null;
  /** Shell the customer actually runs, in order. */
  commands: string[];
  /** Generated configuration they can paste into a file. */
  configFileName: string | null;
  configBody: string | null;
  /** Things that would otherwise be found the hard way. */
  warnings: WarningCode[];
  /** Sections of the guides, so every answer leads somewhere concrete. The
      key is stable; the slug and anchor are resolved per language. */
  docLinks: DocKey[];
}

export type DocKey =
  | 'managed'
  | 'selfHost'
  | 'privateCloud'
  | 'airgapped'
  | 'sizing'
  | 'composeDeploy'
  | 'kubernetes'
  | 'openshift'
  | 'terraform'
  | 'cloudformation';

export type WarningCode =
  | 'burstable'
  | 'bundledDatabase'
  | 'localStorageMultiNode'
  | 'airgappedNoAcme'
  | 'privateCloudPublicApp'
  | 'piiMemory'
  | 'retentionCost';

/** Which provisioners make sense for a given hosting + runtime + cloud. */
export function availableProvisioners(a: Pick<Answers, 'hosting' | 'cloud' | 'runtime'>): Provisioner[] {
  if (a.hosting === 'managed') return [];
  if (a.hosting === 'airgapped') return ['tarball', a.runtime === 'compose' ? 'compose' : 'helm'];
  if (a.runtime === 'kubernetes' || a.runtime === 'openshift') return ['helm'];
  // Compose on a single host. Infrastructure-as-code exists for AWS only —
  // saying "Terraform" for Azure here would be a promise the repository
  // cannot keep.
  const cloudIsAws = a.hosting === 'private' && a.cloud === 'aws';
  return cloudIsAws ? ['terraform', 'cloudformation', 'compose'] : ['compose'];
}

/**
 * Sessions per month → the hardware to ask for.
 *
 * The bands come from the guide's measured table. Peak concurrency is the
 * figure that actually drives CPU, and it is estimated from volume with a
 * deliberately conservative ratio: most traffic lands in a few hours of the
 * day, so a flat average would under-size the box.
 */
export function sizeFor(a: Pick<Answers, 'sessionsPerMonth' | 'retentionDays' | 'piiMasking'>): Sizing {
  const perDay = a.sessionsPerMonth / 30;
  // Peak hour carries roughly a fifth of a day's traffic, and a session lasts
  // a few minutes — so concurrency is about (daily / 5) / 12 five-minute slots.
  const peakConcurrent = Math.max(5, Math.round(perDay / 60));

  let vcpu: number;
  let ramGb: number;
  if (peakConcurrent <= 50) {
    vcpu = 2;
    ramGb = 8;
  } else if (peakConcurrent <= 500) {
    vcpu = 4;
    ramGb = 16;
  } else if (peakConcurrent <= 2000) {
    vcpu = 8;
    ramGb = 32;
  } else {
    vcpu = 16;
    ramGb = 64;
  }

  // The analyzer is CPU-bound and holds ~1GB resident per worker; the guide's
  // measured advice is one worker per 2 vCPU.
  if (a.piiMasking) {
    vcpu += 4;
    ramGb += 4;
  }

  // 1 MB per recorded session, compressed — the middle of the measured range.
  const objectStorageGb = Math.max(1, Math.round((perDay * a.retentionDays * 1) / 1024));

  // Images, the bundled database and 30 GB of headroom for build caches, which
  // is what filled the reference host's disk mid-deploy.
  const diskGb = Math.max(100, 30 + Math.round(objectStorageGb * 0.05) + 50);

  return {
    vcpu,
    ramGb,
    diskGb,
    objectStorageGb,
    peakConcurrent,
    burstableOk: peakConcurrent <= 50 && !a.piiMasking,
  };
}

/** The nearest real machine for each cloud, at or above the computed size. */
const CATALOGUE: Record<Exclude<Cloud, 'vmware' | 'other'>, { vcpu: number; ram: number; name: string }[]> = {
  aws: [
    { vcpu: 2, ram: 8, name: 't3.large' },
    { vcpu: 4, ram: 16, name: 'm6i.xlarge' },
    { vcpu: 8, ram: 16, name: 'c7i.2xlarge' },
    { vcpu: 8, ram: 32, name: 'm6i.2xlarge' },
    { vcpu: 16, ram: 64, name: 'm6i.4xlarge' },
    { vcpu: 32, ram: 128, name: 'm6i.8xlarge' },
  ],
  azure: [
    { vcpu: 2, ram: 8, name: 'Standard_D2as_v5' },
    { vcpu: 4, ram: 16, name: 'Standard_D4as_v5' },
    { vcpu: 8, ram: 32, name: 'Standard_D8as_v5' },
    { vcpu: 16, ram: 64, name: 'Standard_D16as_v5' },
    { vcpu: 32, ram: 128, name: 'Standard_D32as_v5' },
  ],
  gcp: [
    { vcpu: 2, ram: 8, name: 'n2-standard-2' },
    { vcpu: 4, ram: 16, name: 'n2-standard-4' },
    { vcpu: 8, ram: 32, name: 'n2-standard-8' },
    { vcpu: 16, ram: 64, name: 'n2-standard-16' },
    { vcpu: 32, ram: 128, name: 'n2-standard-32' },
  ],
};

export function instanceFor(cloud: Cloud, sizing: Sizing): string | null {
  if (cloud === 'vmware' || cloud === 'other') return null;
  const list = CATALOGUE[cloud];
  const fit = list.find((m) => m.vcpu >= sizing.vcpu && m.ram >= sizing.ramGb);
  return (fit ?? list[list.length - 1]).name;
}

/**
 * Replica counts for a cluster. The collector scales with visitor traffic, the
 * worker with how fast recordings must become watchable; the api with how many
 * people use the product, which is not the same curve.
 */
function replicasFor(sizing: Sizing): { api: number; collector: number; worker: number } {
  const c = sizing.peakConcurrent;
  return {
    api: c > 500 ? 3 : 2,
    collector: c > 2000 ? 6 : c > 500 ? 4 : 2,
    worker: c > 2000 ? 4 : c > 500 ? 2 : 1,
  };
}

function warningsFor(a: Answers, sizing: Sizing): WarningCode[] {
  const w: WarningCode[] = [];
  // Only worth saying where someone might still reach for a t3 or a B-series.
  if (!sizing.burstableOk && (a.cloud === 'aws' || a.hosting === 'self')) w.push('burstable');
  if (!a.managedDatabase && a.hosting !== 'managed') w.push('bundledDatabase');
  if ((a.runtime === 'kubernetes' || a.runtime === 'openshift') && sizing.peakConcurrent > 50) {
    w.push('localStorageMultiNode');
  }
  if (a.hosting === 'airgapped') w.push('airgappedNoAcme');
  if (a.hosting === 'private') w.push('privateCloudPublicApp');
  if (a.piiMasking) w.push('piiMemory');
  if (sizing.objectStorageGb > 500) w.push('retentionCost');
  return w;
}

function commandsFor(a: Answers): string[] {
  switch (a.provisioner) {
    case 'terraform':
      return [
        'cd deploy/terraform',
        'cp terraform.tfvars.example terraform.tfvars   # fill in app_domain + bootstrap_*',
        'terraform init',
        'terraform apply',
      ];
    case 'cloudformation':
      return [
        'aws cloudformation deploy \\',
        '  --template-file deploy/cloudformation/replayops.yaml \\',
        '  --stack-name replayops --capabilities CAPABILITY_IAM \\',
        '  --parameter-overrides AppDomain=replay.example.com \\',
        '      BootstrapEmail=platform@example.com BootstrapPassword=<choose-one>',
      ];
    case 'helm':
      return [
        'helm install replayops ./deploy/helm/replayops \\',
        '  --namespace replayops --create-namespace \\',
        a.runtime === 'openshift'
          ? '  -f deploy/helm/replayops/values.yaml -f deploy/helm/replayops/values-openshift.yaml \\'
          : '  -f my-values.yaml \\',
        '  --set appDomain=replay.example.com',
      ];
    case 'tarball':
      return [
        '# On a machine that CAN build and reach the internet:',
        'REGISTRY=placeholder ./scripts/push-images.sh --save replayops.tar',
        '# Carry it in, then on the target host:',
        'docker load -i replayops.tar',
        'docker compose -f docker-compose.airgapped.yml -f docker-compose.registry.yml up -d',
      ];
    case 'compose':
    default:
      return [
        'cp deploy/aws.env.example deploy/aws.env   # fill in secrets, domain, storage',
        'docker compose -f docker-compose.aws.yml --env-file deploy/aws.env up -d',
      ];
  }
}

/** A starting configuration file, pre-filled with the sizing decisions. */
function configFor(a: Answers, sizing: Sizing, instance: string | null, replicas: ReturnType<typeof replicasFor> | null) {
  if (a.provisioner === 'terraform') {
    return {
      name: 'terraform.tfvars',
      body: [
        `region        = "eu-central-1"`,
        `app_domain    = "replay.example.com"`,
        `instance_type = "${instance ?? 't3.large'}"`,
        `root_volume_size = ${sizing.diskGb}`,
        '',
        `bootstrap_email    = "platform@example.com"`,
        `bootstrap_password = "change-me-after-first-login"`,
        '',
        `use_rds = ${a.managedDatabase}`,
        `recordings_retention_days = ${a.retentionDays}`,
      ].join('\n'),
    };
  }
  if (a.provisioner === 'helm') {
    return {
      name: 'my-values.yaml',
      body: [
        'appDomain: replay.example.com',
        'singleTenant: true',
        '',
        `api:\n  replicas: ${replicas?.api ?? 2}`,
        `collector:\n  replicas: ${replicas?.collector ?? 2}`,
        `worker:\n  replicas: ${replicas?.worker ?? 1}`,
        '',
        a.managedDatabase
          ? 'postgres:\n  enabled: false\nexternalDatabaseUrl: postgresql://replayops:…@pg.internal:5432/replayops?schema=public&sslmode=require'
          : `postgres:\n  enabled: true\n  persistence:\n    size: ${Math.max(20, Math.round(sizing.objectStorageGb * 0.02))}Gi`,
        '',
        sizing.peakConcurrent > 50
          ? `storage:\n  driver: s3\n  s3:\n    bucket: replayops-sessions\n    region: eu-central-1`
          : `storage:\n  driver: local\n  local:\n    size: ${sizing.objectStorageGb}Gi`,
        '',
        a.piiMasking ? 'presidio:\n  enabled: true\n  workers: ' + Math.max(1, Math.floor(sizing.vcpu / 2)) : 'presidio:\n  enabled: false',
      ].join('\n'),
    };
  }
  if (a.provisioner === 'cloudformation') return { name: null, body: null };
  return {
    name: 'deploy/aws.env',
    body: [
      'APP_DOMAIN=replay.example.com',
      'ACME_EMAIL=platform@example.com',
      '',
      `SINGLE_TENANT=true`,
      'REPLAYOPS_BOOTSTRAP_EMAIL=platform@example.com',
      'REPLAYOPS_BOOTSTRAP_PASSWORD=change-me-after-first-login',
      '',
      '# openssl rand -hex 32. Record these in your secret manager',
      'JWT_ACCESS_SECRET=',
      'JWT_REFRESH_SECRET=',
      'SECRETS_KEY=',
      '',
      a.hosting === 'airgapped'
        ? 'STORAGE_DRIVER=s3\nAWS_ENDPOINT=http://minio:9000\nS3_SESSIONS_BUCKET=replayops-sessions\nQUEUE_DRIVER=redis'
        : 'STORAGE_DRIVER=s3\nS3_SESSIONS_BUCKET=replayops-sessions\nQUEUE_DRIVER=sqs',
      '',
      a.piiMasking ? `PRESIDIO_WORKERS=${Math.max(1, Math.floor(sizing.vcpu / 2))}` : '# PII masking disabled',
    ].join('\n'),
  };
}

function docLinksFor(a: Answers): DocKey[] {
  if (a.hosting === 'managed') return ['managed'];
  const links: DocKey[] = [];
  if (a.hosting === 'self') links.push('selfHost');
  if (a.hosting === 'private') links.push('privateCloud');
  if (a.hosting === 'airgapped') links.push('airgapped');

  if (a.runtime === 'kubernetes') links.push('kubernetes');
  else if (a.runtime === 'openshift') links.push('openshift');
  else if (a.provisioner === 'terraform') links.push('terraform');
  else if (a.provisioner === 'cloudformation') links.push('cloudformation');
  else links.push('composeDeploy');

  links.push('sizing');
  return links;
}

export function planInstall(a: Answers): Recommendation {
  const sizing = sizeFor(a);
  const cluster = a.runtime === 'kubernetes' || a.runtime === 'openshift';
  const instance = a.hosting === 'managed' ? null : instanceFor(a.cloud, sizing);
  const replicas = cluster ? replicasFor(sizing) : null;
  const config = configFor(a, sizing, instance, replicas);

  return {
    sizing,
    instance: a.hosting === 'private' ? instance : null,
    replicas,
    commands: a.hosting === 'managed' ? [] : commandsFor(a),
    configFileName: config.name,
    configBody: config.body,
    warnings: a.hosting === 'managed' ? [] : warningsFor(a, sizing),
    docLinks: docLinksFor(a),
  };
}
